import {DBSchema, IDBPDatabase, openDB, OpenDBCallbacks, StoreKey, StoreNames, StoreValue, IndexNames, IndexKey} from 'idb';


// Not injectable because you need to use the constructor/ getDb to instantiate
export class BrowserDbService<DBTypes extends DBSchema | unknown = unknown> {
  readonly db: Promise<IDBPDatabase<DBTypes>>;
  private static dbPrefixName: string = '';

  constructor(
    name: string,
    version: number,
    openDBCallbacks: OpenDBCallbacks<unknown> = {},
    upgradeFinished: Promise<void>,
  ) {
    const prefix = BrowserDbService.getDbPrefixName();
    name = prefix ? `${prefix}/${name}` : name;
    this.db = openDB<DBTypes>(name, version, openDBCallbacks as any)
      .then(async db => {
        const doesNeedUpgrade = db.version < version;

        if (doesNeedUpgrade) {
          await upgradeFinished;
        }

        return db;
      })
      .catch(e => {
        // error is logged by the caller (e.g. MmDbService) via Logger
        throw e;
      });
  }

  static setDbPrefixName(prefix: string) {
    BrowserDbService.dbPrefixName = prefix;
  }

  static getDbPrefixName(): string {
    return BrowserDbService.dbPrefixName;
  }

  /**
   * @param name
   * @param version
   * @param openDBCallbacks
   * @param upgradeFinished Under certain circumstances, the upgrade won't have finished
   * before all the upgrade method has finished. For this reason, you need to pass a
   * promise that indicates when the upgrade has finished. At the time of writing this,
   * the above described behaviour occurs on Firefox and Edge; not Chrome.
   */
  static getDb<T>(
    name: string,
    version: number,
    openDBCallbacks: OpenDBCallbacks<unknown> = {},
    upgradeFinished: Promise<void>,
  ): BrowserDbService<T> {
    return new BrowserDbService(name, version, openDBCallbacks, upgradeFinished);
  }

  async set<Name extends StoreNames<DBTypes>>(
    storeName: Name,
    value: StoreValue<DBTypes, Name>,
    key?: StoreKey<DBTypes, Name> | IDBKeyRange,
  ): Promise<StoreValue<DBTypes, Name>> {
    const db = await this.db;

    // Passing an undefined `key` results in Microsoft Edge errors
    await (key ? db.put(storeName, value, key) : db.put(storeName, value));

    return value;
  }

  async setAll<Name extends StoreNames<DBTypes>>(
    storeName: Name,
    values: Array<StoreValue<DBTypes, Name>>,
  ): Promise<Array<StoreValue<DBTypes, Name>>> {
    const db = await this.db;
    const transaction = db.transaction(storeName, 'readwrite');

    transaction.store.clear();

    values.forEach(kv => {
      transaction.store.put(kv);
    });

    await transaction.done;

    return values;
  }

  async putAll<Name extends StoreNames<DBTypes>>(
    storeName: Name,
    values: Array<StoreValue<DBTypes, Name>>,
  ): Promise<Array<StoreValue<DBTypes, Name>>> {
    const db = await this.db;
    const transaction = db.transaction(storeName, 'readwrite');

    // transaction.store.clear();

    values.forEach(kv => {
      transaction.store.put(kv);
    });

    await transaction.done;

    return values;
  }

  async setAllWithOutOfLineKey<Name extends StoreNames<DBTypes>>(
    storeName: Name,
    values: Array<StoreValue<DBTypes, Name>>,
    getKey: (v: StoreValue<DBTypes, Name>) => StoreKey<DBTypes, Name> | IDBKeyRange,
  ): Promise<Array<StoreValue<DBTypes, Name>>> {
    const db = await this.db;
    const transaction = db.transaction(storeName, 'readwrite');

    transaction.store.clear();

    values.forEach(kv => {
      transaction.store.put(kv, getKey(kv));
    });

    await transaction.done;

    return values;
  }

  async get<Name extends StoreNames<DBTypes>>(
    storeName: Name,
    key: StoreKey<DBTypes, Name> | IDBKeyRange,
    fallback?: null | StoreValue<DBTypes, Name>,
  ): Promise<StoreValue<DBTypes, Name> | null> {
    const db = await this.db;
    const result = await db.get(storeName, key);

    return result || fallback;
  }

  async getAllFromIndex<Name extends StoreNames<DBTypes>, IndexName extends IndexNames<DBTypes, Name>>(
    storeName: Name,
    indexNames: IndexName,
    query: IndexKey<DBTypes, Name, IndexName> | IDBKeyRange,
    fallback?: null | StoreValue<DBTypes, Name>[],
  ): Promise<StoreValue<DBTypes, Name>[] | null> {
    const db = await this.db;
    const result = await db.getAllFromIndex(storeName, indexNames, query);

    return result || fallback;
  }


  async isNew() {
    const db = await this.db;

    return new Promise(async (resolve, reject) => {
        const storeNames = Array.from(db.objectStoreNames); // <-- Fix here

        if (storeNames.length === 0) {
          resolve(true); // No object stores = empty DB
          db.close();
          return;
        }

        const transaction = db.transaction(storeNames, 'readonly');
        const cnts = await Promise.all(storeNames.map(async storeName => {
          const store = transaction.objectStore(storeName);
          const countRequest = store.count();
          return countRequest;
        }));

        resolve(!cnts.some(x => x > 0));
    });
  }

  async getAll<Name extends StoreNames<DBTypes>>(
    storeName: Name,
    fallback?: null | Array<StoreValue<DBTypes, Name>>,
  ): Promise<Array<StoreValue<DBTypes, Name>> | null> {
    const db = await this.db;
    let result: Array<StoreValue<DBTypes, Name>>;

    if ('getAll' in db) {
      result = await db.getAll(storeName);
    } else {
      // Fallback to the traditional cursor approach if getAll isn't supported.
      result = [];
      const transaction = (db as IDBPDatabase<DBTypes>).transaction(storeName, 'readonly');
      const objectStore = transaction.objectStore(storeName);
      let cursor = await objectStore.openCursor();

      while (cursor) {
        result.push(cursor.value);
        cursor = await cursor.continue();
      }
    }

    return result || fallback;
  }

  async remove<Name extends StoreNames<DBTypes>>(
    storeName: Name,
    key: StoreKey<DBTypes, Name> | IDBKeyRange
  ): Promise<void> {
    const db = await this.db;
    return db.delete(storeName, key);
  }

  async countAll<Name extends StoreNames<DBTypes>>(
    storeName: Name
  ): Promise<number> {
    const db = await this.db;
    return db.count(storeName);
  }

  async clear<Name extends StoreNames<DBTypes>>(
    storeName: Name
  ): Promise<void> {
    const db = await this.db;
    return db.clear(storeName);
  }

}
